<template>
  <Teleport to="body">
    <dialog
      v-if="options"
      ref="dialogEl"
      class="confirm-modal"
      :class="[`confirm-modal--${options.variant ?? 'default'}`]"
      :data-testid="options.testId ?? 'confirm-modal'"
      @close="onClose"
      @cancel="onCancel"
      @click="onBackdropClick"
    >
      <div class="confirm-modal__panel" @click.stop>
        <h2 class="confirm-modal__title">{{ options.title }}</h2>
        <p v-if="options.body" class="confirm-modal__body">{{ options.body }}</p>

        <label v-if="options.destructive && options.acknowledgePhrase" class="confirm-modal__acknowledge">
          <span class="confirm-modal__acknowledge-label">
            Type <code>{{ options.acknowledgePhrase }}</code> to confirm:
          </span>
          <input
            v-model="acknowledgeInput"
            type="text"
            :autocomplete="'off'"
            :data-testid="'confirm-modal-acknowledge'"
            class="confirm-modal__acknowledge-input"
            @keydown.enter.prevent="onConfirm"
          />
        </label>

        <div class="confirm-modal__actions">
          <button
            type="button"
            class="btn btn-secondary"
            :data-testid="'confirm-modal-cancel'"
            @click="onCancelClick"
          >
            {{ options.cancelLabel ?? 'Cancel' }}
          </button>
          <button
            type="button"
            :class="['btn', confirmButtonClass]"
            :data-testid="'confirm-modal-confirm'"
            :disabled="!canConfirm"
            @click="onConfirm"
          >
            {{ options.confirmLabel ?? 'Confirm' }}
          </button>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useConfirmStore } from "../stores/confirm";

/**
 * In-app confirmation modal (mounted once in App.vue). Driven by
 * the Pinia confirm store (src/stores/confirm.ts). Renders nothing
 * when the store has no current request.
 *
 * 2026-06-28: added to replace `window.confirm()` in the studio's
 * destructive flows. Browser-native dialogs are unreliable under
 * Cursor's browser automation (the operator can accidentally
 * auto-accept a delete prompt), so destructive actions now use a
 * custom modal that:
 *   - traps focus + handles ESC via the native <dialog> element
 *   - requires an acknowledgement phrase (`acknowledgePhrase`) for
 *     destructive flows before the confirm button enables
 *   - resolves the store's promise with `true` (confirm) / `false`
 *     (cancel / ESC / backdrop click)
 */
const store = useConfirmStore();

const options = computed(() => store.current);
const dialogEl = ref<HTMLDialogElement | null>(null);
const acknowledgeInput = ref("");

const canConfirm = computed(() => {
  const opts = options.value;
  if (!opts) return false;
  if (opts.destructive && opts.acknowledgePhrase) {
    return acknowledgeInput.value.trim() === opts.acknowledgePhrase;
  }
  return true;
});

const confirmButtonClass = computed(() => {
  const variant = options.value?.variant ?? "default";
  if (variant === "danger") return "btn-danger";
  if (variant === "warning") return "btn-warning";
  return "btn-primary";
});

watch(
  () => store.current,
  async (current) => {
    acknowledgeInput.value = "";
    await nextTick();
    if (current && dialogEl.value && !dialogEl.value.open) {
      dialogEl.value.showModal();
    } else if (!current && dialogEl.value?.open) {
      dialogEl.value.close();
    }
  },
  { immediate: true },
);

function onConfirm() {
  if (!canConfirm.value) return;
  store.resolve(true);
}

function onCancelClick() {
  store.resolve(false);
}

function onCancel() {
  // The native <dialog> `cancel` event fires on ESC. The store's
  // `resolve` clears `current`, which closes the dialog via the
  // watcher above. But by then the watcher has already cleared
  // `current` synchronously? No — the watcher runs on next tick.
  // So we must guard against double-resolve: if `current` is null
  // already, ignore.
  if (store.current) store.resolve(false);
}

function onClose() {
  // Triggered when the dialog is closed programmatically (via
  // dialog.close()) OR when the user hits ESC (which the browser
  // fires `cancel` then `close` for). Resolve to false if still
  // pending.
  if (store.current) store.resolve(false);
}

function onBackdropClick(event: MouseEvent) {
  // The backdrop is the <dialog> element itself; the panel sits
  // inside it. Clicks that reach the <dialog> are on the backdrop
  // (clicks on the panel are stopped via @click.stop). Treat
  // backdrop click as cancel.
  if (event.target === dialogEl.value) {
    store.resolve(false);
  }
}
</script>

<style>
.confirm-modal {
  border: none;
  padding: 0;
  background: transparent;
  color: var(--hp-text, #111827);
  max-width: min(560px, calc(100vw - 2rem));
  width: 100%;
}

.confirm-modal::backdrop {
  background: rgba(15, 23, 42, 0.45);
}

.confirm-modal__panel {
  background: var(--hp-surface, #fff);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.confirm-modal__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.3;
}

.confirm-modal__body {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--hp-text-muted, #4b5563);
}

.confirm-modal__acknowledge {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.confirm-modal__acknowledge-label code {
  background: var(--hp-bg, #f3f4f6);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 700;
  color: var(--hp-danger, #dc2626);
}

.confirm-modal__acknowledge-input {
  font-family: inherit;
  font-size: 0.9375rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--hp-border, #e5e7eb);
  border-radius: 6px;
  background: var(--hp-surface, #fff);
  color: inherit;
}

.confirm-modal__acknowledge-input:focus {
  outline: 2px solid var(--hp-primary, #2563eb);
  outline-offset: 1px;
}

.confirm-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.confirm-modal .btn {
  padding: 0.625rem 1rem;
  min-height: var(--tap-target, 44px);
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  font-family: inherit;
}

.confirm-modal .btn-secondary {
  background: var(--hp-surface, #fff);
  color: var(--hp-text, #111827);
  border-color: var(--hp-border, #e5e7eb);
}

.confirm-modal .btn-primary {
  background: var(--hp-primary, #2563eb);
  color: var(--hp-on-primary, #fff);
}

.confirm-modal .btn-danger {
  background: var(--hp-danger, #dc2626);
  color: var(--hp-on-primary, #fff);
}

.confirm-modal .btn-warning {
  background: #d97706;
  color: var(--hp-on-primary, #fff);
}

.confirm-modal .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Variant accent strip — purely cosmetic, reinforces danger/warning. */
.confirm-modal--danger .confirm-modal__panel {
  border-top: 4px solid var(--hp-danger, #dc2626);
}
.confirm-modal--warning .confirm-modal__panel {
  border-top: 4px solid #d97706;
}
.confirm-modal--default .confirm-modal__panel {
  border-top: 4px solid var(--hp-primary, #2563eb);
}
</style>